import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Gift, Copy, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "repticraft_newsletter_seen";
const APPEAR_DELAY_MS = 15000;

const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // If the user arrived via an email "Claim My Code" CTA, open immediately and skip the
    // once-per-visitor gate (they've asked for this explicitly).
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribe") === "1") {
      setOpen(true);
      return;
    }
    if (localStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const markSeen = () => {
    localStorage.setItem(SEEN_KEY, Date.now().toString());
  };

  const dismiss = () => {
    markSeen();
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email });
      if (data.already_used) {
        toast.info(data.message);
        markSeen();
        setOpen(false);
        return;
      }
      setCode(data.code);
      markSeen();
    } catch (err) {
      const msg = err.response?.data?.detail || "Couldn't subscribe right now. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please select manually.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent
        className="sm:max-w-md bg-[#0A0A0A] border border-[#E07A5F]/30 text-[#F4F1DE] p-0 overflow-hidden"
        data-testid="newsletter-popup"
      >
        <DialogTitle className="sr-only">Save 10% on your first enclosure</DialogTitle>
        <DialogDescription className="sr-only">
          Join the ReptiCraft newsletter for build tips and a one-time 10% discount code delivered to your inbox.
        </DialogDescription>
        {/* Accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-[#E07A5F] via-amber-400 to-[#E07A5F]" />

        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-[#F4F1DE]/60 hover:text-[#F4F1DE] transition-colors z-10"
          aria-label="Close"
          data-testid="newsletter-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-7">
          {!code ? (
            <>
              <div className="w-12 h-12 rounded-lg bg-[#E07A5F]/15 border border-[#E07A5F]/30 flex items-center justify-center mb-5">
                <Gift className="w-6 h-6 text-[#E07A5F]" />
              </div>
              <h2 className="text-2xl font-bold font-['Space_Grotesk'] leading-tight mb-2">
                Save 10% on your<br />first enclosure
              </h2>
              <p className="text-sm text-[#F4F1DE]/60 mb-5 leading-relaxed">
                Join the ReptiCraft list for build tips, new stain drops and a one-time discount code delivered to your inbox.
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="bg-[#121212] border-white/10 text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-[#E07A5F]"
                  data-testid="newsletter-email-input"
                  required
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold tracking-wide"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? "Sending..." : "Claim my 10% off"}
                </Button>
              </form>

              <button
                onClick={dismiss}
                className="w-full mt-3 text-xs text-[#F4F1DE]/40 hover:text-[#F4F1DE]/70 transition-colors"
                data-testid="newsletter-decline-btn"
              >
                No thanks, I'll pay full price
              </button>

              <p className="text-[10px] text-[#F4F1DE]/30 mt-4 leading-relaxed">
                One-time discount, single use. Unsubscribe anytime. We never share your email.
              </p>
            </>
          ) : (
            <div data-testid="newsletter-success">
              <div className="w-12 h-12 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold font-['Space_Grotesk'] mb-2">
                You're in!
              </h2>
              <p className="text-sm text-[#F4F1DE]/60 mb-5">
                Use this code at checkout for 10% off your first order. We've also emailed a copy to <span className="text-[#F4F1DE]/80">{email}</span>.
              </p>

              <div
                className="flex items-center justify-between bg-[#121212] border-2 border-dashed border-[#E07A5F]/50 rounded-lg px-4 py-3 mb-5"
                data-testid="newsletter-code-display"
              >
                <span className="font-mono text-xl font-bold text-[#E07A5F] tracking-widest">
                  {code}
                </span>
                <button
                  onClick={copyCode}
                  className="text-[#F4F1DE]/70 hover:text-[#E07A5F] transition-colors"
                  aria-label="Copy code"
                  data-testid="newsletter-copy-code-btn"
                >
                  {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>

              <Button
                onClick={dismiss}
                className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold"
                data-testid="newsletter-done-btn"
              >
                Start shopping
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterPopup;
